import React, { useState, useEffect } from 'react';
import { FaBell, FaExclamationTriangle, FaCarCrash, FaShieldAlt, FaTools, FaCalendarCheck } from 'react-icons/fa';
import axios from 'axios';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';

const InsuranceExpirationAlert = ({ token }) => {
  const [expiringVehicles, setExpiringVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExpiringVehicles();
  }, []);

  const fetchExpiringVehicles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('https://rent-server.create-site.gr/api/cars/insurance/expiring', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setExpiringVehicles(response.data || []);
    } catch (err) {
      console.error('Error fetching expiring insurance information:', err);
      setError('Could not load expiration data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate days until expiration
  const getDaysUntilExpiration = (endDate) => {
    const now = moment();
    const expiry = moment(endDate);
    return expiry.diff(now, 'days');
  };

  // Get vehicles with insurance expiring within 30 days
  const upcomingExpirations = expiringVehicles.filter(vehicle => {
    const daysLeft = getDaysUntilExpiration(vehicle.insurance.endDate);
    return daysLeft >= 0 && daysLeft <= 30;
  });

  // Get vehicles with already expired insurance
  const expiredVehicles = expiringVehicles.filter(vehicle => {
    const daysLeft = getDaysUntilExpiration(vehicle.insurance.endDate);
    return daysLeft < 0;
  });

  // Determine color class based on days until expiration
  const getExpirationColorClass = (endDate) => {
    const daysLeft = getDaysUntilExpiration(endDate);
    
    if (daysLeft < 0) return 'text-red-600 bg-red-50';
    if (daysLeft <= 7) return 'text-red-600 bg-red-50';
    if (daysLeft <= 14) return 'text-orange-600 bg-orange-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  // Format days until expiration display
  const formatDaysLeft = (endDate) => {
    const daysLeft = getDaysUntilExpiration(endDate);
    
    if (daysLeft < 0) return `Expired ${Math.abs(daysLeft)} days ago`;
    if (daysLeft === 0) return 'Expires today';
    if (daysLeft === 1) return 'Expires tomorrow';
    return `Expires in ${daysLeft} days`;
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const totalAlerts = upcomingExpirations.length + expiredVehicles.length;

  if (loading) {
    return (
      <div className="relative">
        <button className="relative p-2 text-gray-600 hover:text-gray-800">
          <FaBell className="w-6 h-6" />
          <div className="absolute top-1 right-1 w-3 h-3 rounded-full bg-gray-300 animate-pulse"></div>
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative">
        <button className="relative p-2 text-red-600 hover:text-red-800">
          <FaBell className="w-6 h-6" />
          <div className="absolute top-1 right-1 w-3 h-3 rounded-full bg-red-500"></div>
        </button>
      </div>
    );
  }

  if (totalAlerts === 0) {
    return (
      <div className="relative">
        <button className="relative p-2 text-gray-600 hover:text-gray-800">
          <FaBell className="w-6 h-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button 
        className="relative p-2 text-gray-600 hover:text-gray-800 focus:outline-none"
        onClick={toggleDropdown}
      >
        <FaBell className="w-6 h-6" />
        {totalAlerts > 0 && (
          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
            {totalAlerts}
          </div>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-md shadow-lg z-20 border border-gray-200">
          <div className="p-3 border-b border-gray-200 bg-blue-800 text-white rounded-t-md">
            <h3 className="font-semibold flex items-center">
              <FaShieldAlt className="mr-2" /> Insurance Alerts
            </h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {expiredVehicles.length > 0 && (
              <div className="p-3 border-b border-gray-200 bg-red-50">
                <h4 className="font-medium text-red-800 mb-2 flex items-center">
                  <FaExclamationTriangle className="mr-2" /> Expired Insurance ({expiredVehicles.length})
                </h4>
                <ul className="space-y-2">
                  {expiredVehicles.slice(0, showAll ? expiredVehicles.length : 3).map(vehicle => (
                    <li key={vehicle._id} className="p-2 bg-white rounded border border-red-200 text-sm">
                      <div className="flex justify-between">
                        <div className="font-medium">{vehicle.carMake} {vehicle.carModel}</div>
                        <div className="text-red-600 font-bold">{formatDaysLeft(vehicle.insurance.endDate)}</div>
                      </div>
                      <div className="flex justify-between text-gray-600 text-xs mt-1">
                        <div>{vehicle.licensePlate}</div>
                        <div>{vehicle.insurance.provider}</div>
                      </div>
                    </li>
                  ))}
                  {!showAll && expiredVehicles.length > 3 && (
                    <li className="text-center text-sm text-gray-500 hover:underline cursor-pointer" onClick={() => setShowAll(true)}>
                      Show {expiredVehicles.length - 3} more expired...
                    </li>
                  )}
                </ul>
              </div>
            )}

            {upcomingExpirations.length > 0 && (
              <div className="p-3">
                <h4 className="font-medium text-gray-800 mb-2 flex items-center">
                  <FaCalendarCheck className="mr-2" /> Upcoming Expirations ({upcomingExpirations.length})
                </h4>
                <ul className="space-y-2">
                  {upcomingExpirations.slice(0, showAll ? upcomingExpirations.length : 5).map(vehicle => (
                    <li 
                      key={vehicle._id} 
                      className={`p-2 rounded border text-sm ${getExpirationColorClass(vehicle.insurance.endDate)}`}
                    >
                      <div className="flex justify-between">
                        <div className="font-medium">{vehicle.carMake} {vehicle.carModel}</div>
                        <div className="font-medium">{formatDaysLeft(vehicle.insurance.endDate)}</div>
                      </div>
                      <div className="flex justify-between text-gray-600 text-xs mt-1">
                        <div>{vehicle.licensePlate}</div>
                        <div>Expires: {moment(vehicle.insurance.endDate).format('DD/MM/YYYY')}</div>
                      </div>
                    </li>
                  ))}
                  {!showAll && upcomingExpirations.length > 5 && (
                    <li className="text-center text-sm text-gray-500 hover:underline cursor-pointer" onClick={() => setShowAll(true)}>
                      Show {upcomingExpirations.length - 5} more upcoming...
                    </li>
                  )}
                </ul>
              </div>
            )}

            {showAll && (
              <div className="text-center p-2 border-t border-gray-200">
                <button 
                  className="text-sm text-blue-600 hover:underline" 
                  onClick={() => setShowAll(false)}
                >
                  Show less
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceExpirationAlert;
